import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Coordinate } from 'recharts/types/util/types';
import {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { Typography } from '@libs/components';
import { formatDate } from '@libs/hooks';
import { CurrencySymbol } from '@libs/types';
import { formatPrice, hasAnyFieldEmpty } from '@libs/helpers';

import {
  chartMargins,
  Container,
  IconWrapper,
  OpacityContainer,
  TooltipRects,
} from './styles';

type CustomToolTipProps = {
  tooltipPrecision?: number;
  payload?: Payload<ValueType, NameType>[];
  label?: string | number;
  currencySymbol?: CurrencySymbol;
  icon?: JSX.Element | string;
  title?: string;
  coordinate?: Partial<Coordinate>;
  chartRects: TooltipRects;
  shouldHaveFadeEffect?: boolean;
  amountColor?: string;
  yAxisOrientation?: 'left' | 'right';
  chartMargin?: chartMargins;
  customDateFormat?: string;
  showAmountPrefix?: boolean;
};

export const ChartTooltip: React.FC<CustomToolTipProps> = ({
  payload,
  label,
  tooltipPrecision,
  currencySymbol,
  icon,
  title,
  coordinate,
  chartRects,
  shouldHaveFadeEffect,
  amountColor,
  yAxisOrientation,
  chartMargin,
  customDateFormat,
  showAmountPrefix,
}) => {
  const [containerDiv, setContainerDiv] = useState<HTMLDivElement | null>(null);
  const { colors } = useTheme();

  const elementsToRender = useMemo(() => {
    const iconToRender =
      typeof icon === 'string' ? <img src={icon} alt="icon" /> : icon || null;

    const titleToRender = title ? (
      <Typography type="bodyButton" fontFamily="primaryBold">
        {title}
      </Typography>
    ) : null;

    return { iconToRender, titleToRender };
  }, [icon, title]);

  if (!payload || !payload.length) return null;

  const rawValue = payload[0]?.value;
  const value = typeof rawValue === 'number' ? rawValue : Number(rawValue) || 0;

  const fixedValue: string | number = tooltipPrecision
    ? value.toFixed(tooltipPrecision)
    : value;

  const dateFormat = customDateFormat || 'DD/MM/YYYY';

  const formattedDate =
    label !== undefined ? formatDate(dateFormat, String(label)) : '';

  const formattedPrice = formatPrice(fixedValue);

  const shouldRenderIconWrapper = !!icon || !!title;

  const containerWidth = containerDiv?.getBoundingClientRect().width || 0;

  const xContainerLeft = coordinate?.x ?? 0;

  const { iconToRender, titleToRender } = elementsToRender;

  const defaultAmountColor =
    value >= 0 ? colors.text.success : colors.text.critical;

  if (hasAnyFieldEmpty(chartRects)) {
    return null;
  }

  const amountPrefix = showAmountPrefix && +formattedPrice > 0 ? '+' : '';

  return (
    <>
      <Container
        ref={setContainerDiv}
        containerWidth={containerWidth}
        xCoordinate={xContainerLeft}
        chartRects={chartRects}
      >
        {shouldRenderIconWrapper && (
          <IconWrapper>
            {iconToRender}
            {titleToRender}
          </IconWrapper>
        )}

        <Typography
          type="bodyButton"
          color={colors.text.secondary}
          fontFamily="primaryRegular"
        >
          {formattedDate}
        </Typography>
        <Typography
          type="bodyButton"
          fontFamily="primaryBold"
          color={amountColor || defaultAmountColor}
        >
          {amountPrefix}
          {formattedPrice}
          {currencySymbol}
        </Typography>
      </Container>
      {shouldHaveFadeEffect && (
        <OpacityContainer
          xCoordinate={xContainerLeft}
          chartRects={chartRects}
          yAxisOrientation={yAxisOrientation}
          margin={chartMargin}
        />
      )}
    </>
  );
};
